import type { IComponentController, IOnChangesObject } from 'angular';
import template from './interactive-filters-config-panel.component.html';
import { DashboardFiltersService, DefaultValueType, InteractiveFilterParams, PDRReportsFilterOptionPickupItem } from '../dashboard-filters.service';
import type { DataQuery, DataQueryColumn } from '../../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/contracts';
import { MxObjectPropertyType } from '../../Shell/Pandora/model/object';
import type { WidgetInstance } from '../dashboard.service';
import type { InteractiveFilter } from '../InteractiveFilter.model';
import type { ColumnDefinitionService } from '../../Shell/PandoraSolutionBuilderAgent/services/column-definition-service';
import type { I18n } from '../../i18n/mx-internationalization';

interface ReportFilterOption {
	Title: string;
	Name: string;
	AttributeType: number;
	Value: number;
}

interface DefaultValueTypeOption {
	Title: string;
	Value: DefaultValueType;
}

interface FilterConfigItem {
	params: InteractiveFilterParams;
	filter: InteractiveFilter;
	defaultValueTypes: DefaultValueTypeOption[];
	reportFilterOptions: ReportFilterOption[];
	userRelatedValues: DataQueryColumn[];
	expanded: boolean;
	id: number;
}

export class InteractiveFiltersConfigPanelController implements IComponentController {
	public filtersParams: InteractiveFilterParams[] = [];
	public items: FilterConfigItem[] = [];
	public dataQuery?: DataQuery;
	public userRelatedDataQueryPromise?: Promise<DataQuery>;
	public dataQueryId?: string;
	public datasetViewObjId?: string;
	public reportFilterOptionMap: Map<number, PDRReportsFilterOptionPickupItem> = new Map();

	private filtersCache: Record<string, FilterConfigItem[]> = {};

	static readonly $inject = ['mx.sb.Dashboard.DashboardFiltersService', 'mx.SolutionBuilderAgent.mxColumnDefinitionService', 'mx.internationalization'];

	constructor(
		private readonly dashboardFiltersService: DashboardFiltersService,
		private readonly columnDefinitionService: ReturnType<typeof ColumnDefinitionService>,
		private readonly i18n: I18n
	) {
		this.dashboardFiltersService.getReportsFilterOptionPickupEnum().then((pickup) => {
			this.reportFilterOptionMap = new Map((pickup.Values ?? []).map((option) => [option.Value, option]));
		});
	}

	async $onChanges(onChangesObj: IOnChangesObject) {
		if (onChangesObj.dataQueryId || onChangesObj.datasetViewObjId) {
			await this.prepareDataQuery();

			if (onChangesObj.dataQueryId && !onChangesObj.dataQueryId.isFirstChange()) {
				if (this.filtersCache[onChangesObj.dataQueryId.currentValue]) {
					this.items = this.filtersCache[onChangesObj.dataQueryId.currentValue];
				} else {
					this.items = [];
					this.filtersCache[onChangesObj.dataQueryId.currentValue] = this.items;
				}

				this.syncFilterParams();
			}
		}

		if (onChangesObj.filtersParams) {
			if (!Array.isArray(this.filtersParams)) {
				console.error('`filtersParams` must be an array');
			}
			const filters = await this.prepareFilters(this.filtersParams);
			this.items = await Promise.all(
				filters.map<Promise<FilterConfigItem>>(async (filter, index) => {
					const item = {
						filter,
						params: this.filtersParams[index],
						defaultValueTypes: [],
						reportFilterOptions: await this.getFilterOptions(this.filtersParams[index].ColumnName),
						userRelatedValues: await this.getUserRelatedColumns(this.filtersParams[index].ColumnName),
						expanded: false,
						id: Math.round(1000000 * Math.random()),
					};
					this.setDefaultValueTypes(item);
					return item;
				})
			);
			this.filtersCache[this.dataQueryId ?? ''] = this.items;
		}
	}

	private async prepareDataQuery() {
		this.dataQuery = this.dataQueryId ? ((await this.columnDefinitionService.getGridDefinitionById(this.dataQueryId, this.datasetViewObjId)) as DataQuery) : undefined;
	}

	private async getUserRelatedDataQuery() {
		this.userRelatedDataQueryPromise = this.userRelatedDataQueryPromise || this.dashboardFiltersService.getUserRelatedDataQuery();
		return await this.userRelatedDataQueryPromise;
	}

	private async getUserRelatedColumns(columnName: string) {
		if (!this.dataQuery) {
			return [];
		}

		const targetColumn = this.dataQuery.getColumnByName(columnName) as DataQueryColumn;
		const dataQuery = await this.getUserRelatedDataQuery();
		return dataQuery.Columns.filter((column: DataQueryColumn) => {
			if (column.AttributeValueType !== targetColumn.AttributeValueType) {
				return false;
			}

			if (targetColumn.IsPickup || targetColumn.IsEnum) {
				return column.ReferenceTo === targetColumn.ReferenceTo || this.guessReferenceToFromColumn(column);
			}

			if (targetColumn.AttributeValueType === MxObjectPropertyType.Guid.name && targetColumn.Expression.toLowerCase() === 'id') {
				return column.ReferenceTo === targetColumn.EntityClassName || this.guessReferenceToFromColumn(column);
			}

			return false;
		});
	}

	private guessReferenceToFromColumn(column: DataQueryColumn): string | undefined {
		return column.Expression.trim().match(/^(?:Join|SubQuery)\(\s*(\w+).*/i)?.[1];
	}

	private setDefaultValueTypes(item: FilterConfigItem) {
		const getTitle = (title: string) =>
			((this.i18n.get(title) as string) ?? '')
				.split(' ')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ');

		item.defaultValueTypes = [
			{
				Title: getTitle('solutionBuilderAgent.components.mx-charts.a11y.series.nullPointValue'),
				Value: 'none',
			},
			{
				Title: getTitle('solutionBuilder.components.mx-designer-styles-editor.staticValue'),
				Value: 'static',
			},
		];

		if (item.userRelatedValues.length) {
			item.defaultValueTypes.push({
				Title: getTitle('shell.dashboards.config.userRelated'),
				Value: 'user-related',
			});
		}
	}

	private async prepareFilters(filtersParams: InteractiveFilterParams[]) {
		if (!this.dataQuery) {
			return [];
		}

		filtersParams.forEach((filterParams) => {
			if (typeof filterParams.DefaultValueType === 'undefined') {
				filterParams.DefaultValueType = 'none';
			}
		});
		const filters = await this.dashboardFiltersService.getInteractiveFiltersFromConfig({} as WidgetInstance, filtersParams, this.dataQuery);
		filters.forEach((filter, index) => {
			if (this.dashboardFiltersService.isStaticDynamicFilter(filtersParams[index])) {
				filter.setValue(filtersParams[index].DefaultValue, '');
			}
			filter.title = this.i18n.get('shell.common.defaultValue');
			filter.required = false;
			filter.onChange(() => {
				filtersParams[index].DefaultValue = filter.value;
			});
		});
		return filters;
	}

	private syncFilterParams() {
		this.filtersParams.splice(0, this.filtersParams.length, ...this.items.map((item) => item.params));
	}

	private async getFilterOptions(columnName: string) {
		if (!this.dataQuery) {
			return [];
		}
		const column = this.dataQuery.getColumnByName(columnName) as DataQueryColumn;
		const pickup = await this.dashboardFiltersService.getReportsFilterOptionPickupEnum();
		return (
			pickup.Values?.map<ReportFilterOption>((option) => ({
				Title: option.DisplayString,
				Name: option.Name,
				AttributeType: option.AttributeType,
				Value: option.Value,
			})).filter(({ AttributeType }) => {
				switch (column.AttributeValueType) {
					case MxObjectPropertyType.Boolean.name:
						return AttributeType === 1;
					case MxObjectPropertyType.Integer.name:
						return AttributeType === 4 || AttributeType === 6;
					case MxObjectPropertyType.Date.name:
						return AttributeType === 2;
					case MxObjectPropertyType.Guid.name:
						return AttributeType === 3;
					default:
						return true;
				}
			}) ?? []
		);
	}

	removeFilter(removedItem: FilterConfigItem) {
		const itemIndex = this.items.indexOf(removedItem);
		if (itemIndex >= 0) {
			this.items.splice(itemIndex, 1);
		}
		this.syncFilterParams();
	}

	async addFilter() {
		if (!this.dataQuery) {
			return;
		}

		const existColumns = this.items.map((item) => item.params.ColumnName);

		const newColumn: DataQueryColumn = this.dataQuery.Columns.find((column) => !existColumns.includes(column.Name)) as DataQueryColumn;
		const options = await this.getFilterOptions(newColumn.Name);

		const params: InteractiveFilterParams = {
			ColumnName: newColumn.Name,
			ReportFilter: options[0]?.Value,
			DefaultValueType: 'none',
			Required: false,
		};
		const filters = await this.prepareFilters([params]);
		const newItem: FilterConfigItem = {
			filter: filters[0],
			params,
			reportFilterOptions: options,
			defaultValueTypes: [],
			userRelatedValues: await this.getUserRelatedColumns(params.ColumnName),
			expanded: true,
			id: Math.round(1000000 * Math.random()),
		};
		this.setMostSuitableReportFilterOption(newItem);
		this.setDefaultValueTypes(newItem);
		this.items.push(newItem);
		this.syncFilterParams();
	}

	async onFilterParamsColumnChanged(changedItem: FilterConfigItem) {
		changedItem.reportFilterOptions = await this.getFilterOptions(changedItem.params.ColumnName);
		await Promise.resolve(); //hack to update mx-select with new items and new ng-model correctly
		this.setMostSuitableReportFilterOption(changedItem);

		changedItem.userRelatedValues = await this.getUserRelatedColumns(changedItem.params.ColumnName);
		if (this.dashboardFiltersService.isUserRelatedDynamicFilter(changedItem.params)) {
			await Promise.resolve(); //hack to update mx-select with new items and new ng-model correctly
			this.setMostSuitableUserRelatedValue(changedItem);
		}

		this.setDefaultValueTypes(changedItem);
		changedItem.filter.clear();
		await this.onFilterParamsChanged(changedItem);
	}

	private setMostSuitableReportFilterOption(item: FilterConfigItem) {
		const columnName = item.params.ColumnName.toLowerCase();
		const columnTitle = this.dataQuery?.getColumnByName(item.params.ColumnName)?.Title.toLowerCase();

		const mostSuitableColumn = this.getMostSuitableOption(item.reportFilterOptions, columnName, columnTitle);
		item.params.ReportFilter = mostSuitableColumn?.Value;
	}

	private setMostSuitableUserRelatedValue(item: FilterConfigItem) {
		const columnTitle = this.dataQuery?.getColumnByName(item.params.ColumnName)?.Title;
		const mostSuitableColumn = this.getMostSuitableOption(item.userRelatedValues, item.params.ColumnName, columnTitle);
		item.params.DefaultValue = mostSuitableColumn?.Name;
	}

	private getMostSuitableOption<T extends { Name: string; Title: string }>(options: T[], name: string, title: string) {
		if (options.length <= 1) {
			return options[0];
		}
		name = name.toLowerCase();
		title = title.toLowerCase();
		const exactColumnName = options.find((option) => option.Name.toLowerCase() === name);
		const similarColumnName = options.find((option) => option.Name.toLowerCase().includes(name));

		const exactColumnTitle = options.find((option) => option.Title.toLowerCase() === title);
		const similarColumnTitle = options.find((option) => option.Title.toLowerCase().includes(title));
		return exactColumnName ?? similarColumnName ?? exactColumnTitle ?? similarColumnTitle ?? options[0];
	}

	async onFilterParamsReportFilterChanged(changedItem: FilterConfigItem) {
		await this.onFilterParamsChanged(changedItem);
	}

	private onIsMultiPickerChanged(changedItem: FilterConfigItem) {
		changedItem.params.IsMultiPicker = changedItem.filter.isMultiPicker;
	}

	private async onFilterParamsChanged(changedItem: FilterConfigItem) {
		const filters = await this.prepareFilters([changedItem.params]);
		changedItem.filter = filters[0];
	}

	async onDefaultValueTypeChanged(changedItem: FilterConfigItem) {
		if (this.dashboardFiltersService.isNonValueDynamicFilter(changedItem.params)) {
			changedItem.params.DefaultValue = undefined;
		} else if (this.dashboardFiltersService.isStaticDynamicFilter(changedItem.params)) {
			changedItem.params.DefaultValue = changedItem.filter.value;
		} else if (this.dashboardFiltersService.isUserRelatedDynamicFilter(changedItem.params)) {
			changedItem.userRelatedValues = await this.getUserRelatedColumns(changedItem.params.ColumnName);
			const column = changedItem.userRelatedValues.find((column) => column.Name === changedItem.params.ColumnName);
			if (column) {
				changedItem.params.DefaultValue = column.Name;
			}
		}
	}
}

export const InteractiveFiltersConfigPanelComponent = {
	name: 'mxInteractiveFiltersConfigPanel',
	template,
	controller: InteractiveFiltersConfigPanelController,
	controllerAs: 'vm',
	bindings: {
		filtersParams: '<value',
		dataQueryId: '<value2',
		datasetViewObjId: '<value3',
	},
};
